/* eslint-disable @typescript-eslint/ban-ts-comment */
/// <reference types="jest" />

/**
 * Helper function to mock window.location in Jest tests
 * Required for Nx v21.6.2+ where jsdom makes window.location non-configurable
 *
 * @param mockLocation - Partial Location object with properties to mock
 * @returns Object with restore function to clean up after tests
 *
 * @example
 * ```ts
 * const { restore } = mockWindowLocation({ href: 'http://localhost', reload: jest.fn() })
 * // ... your tests
 * restore()
 * ```
 */
export function mockWindowLocation(mockLocation: Partial<Location>) {
  const originalLocation = global.window?.location
  const originalHref = originalLocation?.href

  // Ensure window exists
  if (typeof global.window === 'undefined') {
    // @ts-ignore
    global.window = {}
  }

  // Build URL from mockLocation properties if href is not provided
  function buildUrlFromProperties(props: Partial<Location>): string {
    if (props.href) {
      return props.href
    }

    const protocol = props.protocol || 'http:'
    const host = props.host || 'localhost'
    const pathname = props.pathname || '/'
    const search = props.search || ''
    const hash = props.hash || ''

    return `${protocol}//${host}${pathname}${search}${hash}`
  }

  const builtHref = buildUrlFromProperties(mockLocation)
  let baseUrl: URL

  try {
    baseUrl = new URL(builtHref)
  } catch (error) {
    baseUrl = new URL('http://localhost/')
  }

  // Check if methods are needed
  const needsMethodMocking = !!(
    mockLocation.reload ||
    mockLocation.replace ||
    mockLocation.assign
  )

  // Create mock location object with all properties
  // Only include methods if they're explicitly provided (for method mocking)
  const mockLocationObject: any = {
    href: mockLocation.href || builtHref,
    protocol: mockLocation.protocol || baseUrl.protocol,
    host: mockLocation.host || baseUrl.host,
    hostname: mockLocation.hostname || baseUrl.hostname,
    port: mockLocation.port || baseUrl.port,
    pathname: mockLocation.pathname || baseUrl.pathname,
    search: mockLocation.search || baseUrl.search,
    hash: mockLocation.hash || baseUrl.hash,
    origin: baseUrl.origin,
    toString: () => mockLocationObject.href,
  }

  // Only add methods if they're explicitly provided (for method mocking attempts)
  if (needsMethodMocking) {
    mockLocationObject.reload = mockLocation.reload || jest.fn()
    mockLocationObject.replace = mockLocation.replace || jest.fn()
    mockLocationObject.assign = mockLocation.assign || jest.fn()
  }

  // Try to access jsdom instance (from jest-environment-jsdom-global)
  const jsdomInstance = (global as any).jsdom

  // Strategy 1: Try to delete and redefine (works if configurable)
  try {
    Reflect.deleteProperty(global.window, 'location')
    Object.defineProperty(global.window, 'location', {
      value: mockLocationObject,
      writable: true,
      configurable: true,
      enumerable: true,
    })
    // Success!
    return {
      restore: () => {
        try {
          Reflect.deleteProperty(global.window, 'location')
          if (originalLocation) {
            Object.defineProperty(global.window, 'location', {
              value: originalLocation,
              writable: true,
              configurable: true,
            })
          }
        } catch (error) {
          // Ignore restore errors
        }
      },
    }
  } catch (deleteError) {
    // Delete failed, continue to next strategy
  }

  // Strategy 2: If methods needed, try to shadow (for prototype properties)
  if (needsMethodMocking) {
    try {
      Object.defineProperty(global.window, 'location', {
        value: mockLocationObject,
        writable: true,
        configurable: true,
        enumerable: true,
      })
      // Success!
      return {
        restore: () => {
          try {
            if (originalLocation) {
              Object.defineProperty(global.window, 'location', {
                value: originalLocation,
                writable: true,
                configurable: true,
              })
            }
          } catch (error) {
            // Ignore restore errors
          }
        },
      }
    } catch (shadowError) {
      // Can't shadow - methods can't be mocked in this environment
      console.warn(
        'mockWindowLocation: Cannot mock window.location methods in Jest 30/jsdom 26. ' +
          'Consider refactoring tests to not rely on method mocking.'
      )
      return {
        restore: jest.fn(),
      }
    }
  }

  // Strategy 3: Use jsdom.reconfigure() for URL-only changes (no methods)
  if (jsdomInstance && typeof jsdomInstance.reconfigure === 'function') {
    try {
      jsdomInstance.reconfigure({ url: builtHref })
      return {
        restore: () => {
          try {
            if (originalHref && jsdomInstance.reconfigure) {
              jsdomInstance.reconfigure({ url: originalHref })
            }
          } catch (error) {
            // Ignore restore errors
          }
        },
      }
    } catch (reconfigureError) {
      // Reconfigure failed
    }
  }

  // All strategies failed
  console.warn(
    'mockWindowLocation: Unable to mock window.location. Mock will not be applied.'
  )
  return {
    restore: jest.fn(),
  }
}
